import { defineStore } from 'pinia'
import { ref, readonly } from 'vue'

export const useAppStore = defineStore('app', () => {
  const darkMode = ref(false)
  const sidebarCollapsed = ref(false)
  const mobileMenuOpen = ref(false)
  const currentView = ref('dashboard')
  const isLoading = ref(false)
  const notifications = ref<
    Array<{
      id: string
      type: 'success' | 'error' | 'warning' | 'info'
      title: string
      message: string
      autoClose?: boolean
      duration?: number
    }>
  >([])

  const initDarkMode = () => {
    // Força sempre modo claro
    darkMode.value = false
    document.documentElement.classList.remove('dark')

    // Remove qualquer configuração salva
    localStorage.removeItem('darkMode')

    // Garante que a classe dark seja removida mesmo se adicionada por outros scripts
    const observer = new MutationObserver(() => {
      if (document.documentElement.classList.contains('dark')) {
        document.documentElement.classList.remove('dark')
      }
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })

    // Para o observer após 5 segundos para não impactar performance
    setTimeout(() => observer.disconnect(), 5000)
  }

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed.value.toString())
  }

  const initSidebar = () => {
    const stored = localStorage.getItem('sidebarCollapsed')
    if (stored === 'true') {
      sidebarCollapsed.value = true
    }
  }

  const toggleMobileMenu = () => {
    mobileMenuOpen.value = !mobileMenuOpen.value
  }

  const closeMobileMenu = () => {
    mobileMenuOpen.value = false
  }

  const setCurrentView = (view: string) => {
    currentView.value = view
    closeMobileMenu()
  }

  const setLoading = (loading: boolean) => {
    isLoading.value = loading
  }

  const addNotification = (notification: Omit<(typeof notifications.value)[0], 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9)
    const newNotification = {
      id,
      autoClose: true,
      duration: 5000,
      ...notification
    }

    notifications.value.push(newNotification)

    if (newNotification.autoClose) {
      setTimeout(() => {
        removeNotification(id)
      }, newNotification.duration)
    }

    return id
  }

  const removeNotification = (id: string) => {
    const index = notifications.value.findIndex(n => n.id === id)
    if (index >= 0) {
      notifications.value.splice(index, 1)
    }
  }

  const clearNotifications = () => {
    notifications.value = []
  }

  return {
    darkMode: readonly(darkMode),
    sidebarCollapsed: readonly(sidebarCollapsed),
    mobileMenuOpen: readonly(mobileMenuOpen),
    currentView: readonly(currentView),
    isLoading: readonly(isLoading),
    notifications: readonly(notifications),
    initDarkMode,
    toggleSidebar,
    initSidebar,
    toggleMobileMenu,
    closeMobileMenu,
    setCurrentView,
    setLoading,
    addNotification,
    removeNotification,
    clearNotifications
  }
})
